<template>
    <div>
        <v-form
            v-if="!isPollVoted"
            :class="{'poll-form px-4': true, 'poll-form-event': !postId}"
            ref="form"
            v-model="valid"
            lazy-validation
            @submit.prevent="validateAndSubmit"
        >
            <p v-if="!postId" class="poll-form__title">
                {{ poll.poll_text }}
            </p>
            <v-checkbox
                v-for="answer in poll.pollsAnswers"
                :key="answer.poll_answer_id"
                class="poll-form__answer pa-0 ma-0 pb-3"
                :label="answer.poll_answer"
                :value="answer.poll_answer_id"
                v-model="fields.answer"
                :ripple="false"
                hide-details
                :disabled="pollsLoading"
            />
            <div class="d-flex align-center" v-if="customAnswerAllowed">
                <v-checkbox
                    class="poll-form__answer pa-0 ma-0 pb-3"
                    v-model="fields.answer"
                    label="Your answer:"
                    value="custom"
                    :ripple="false"
                    hide-details
                    :disabled="pollsLoading"
                />
                <v-text-field
                    class="poll-form__custom-answer pa-0 ma-0 mb-3 ml-2"
                    v-model="fields.customAnswer"
                    hide-details
                    :disabled="pollsLoading"
                />
            </div>
            <div style="position: relative;">
                <p
                    v-if="isDirty || isEventVoteDirty"
                    class="poll-form__error"
                >
                    <span v-if="(typeof isCheckboxesValid === 'string')">
                        {{ isCheckboxesValid }}
                    </span>
                    <span v-else-if="!customAnswerIsValid">
                        Custom answer cannot be empty
                    </span>
                </p>
                <v-btn
                    v-if="postId"
                    class="poll-form__submit-btn elevation-0 mt-4"
                    :class="{'poll-form__submit-btn-disabled': !isSubmitEnabled}"
                    @click="handleSubmit"
                    :loading="isPollVoting"
                    :ripple="false"
                >
                    Vote
                </v-btn>
            </div>
        </v-form>
        <v-card
            v-else-if="poll.display_poll"
            :class="{'poll-result px-4 elevation-0': true, 'poll-result-event': !postId}"
        >
            <p v-if="!postId" class="poll-form__title">
                {{ poll.poll_text }}
            </p>
            <v-card-text
                v-for="answer in poll.pollsAnswers"
                :key="answer.poll_answer_id"
                class="d-flex align-center mb-3"
            >
                <span class="poll-result__percentage mr-3">
                    {{ calcPercentage(answer) }} %
                </span>
                <div class="d-flex flex-column justify-center flex-grow-1">
                    <span class="poll-result__answer mb-1">
                        {{ answer.is_custom ? 'Custom answer' : answer.poll_answer }}
                    </span>
                    <v-progress-linear
                        :value="+calcPercentage(answer)"
                        class="poll-result__answer-scale"
                        height="8"
                        rounded
                        :color="styles.navigation_bar_tint"
                    ></v-progress-linear>
                </div>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Form from '@/mixins/Form'
import Dwh from '@/mixins/Dwh'

const AT_LEAST_TYPE = 0
const AT_MOST_TYPE = 1
const EXACTLY_TYPE = 2

export default {
    mixins: [Form, Dwh],
    props: {
        poll: {
            type: Object,
            default: () => {}
        },
        postId: {
            type: Number,
            default: null
        },
        post: {
            type: Object,
            default: () => {}
        },
        answers: {
            type: Array,
            default: () => []
        },
        isEventVoteDirty: {
            type: Boolean,
            default: false
        },
        pollsLoading: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            fields: {
                answer: [],
                customAnswer: ''
            },
            isDirty: false
        }
    },
    computed: {
        ...mapGetters('settings', ['styles']),
        ...mapGetters('post', ['isPollVoting']),

        isPollVoted() {
            return this.poll.vote
        },
        customAnswerAllowed() {
            return this.poll.allow_custom_answer
        },
        isSubmitEnabled() {
            return Array.isArray(this.fields.answer) ? this.fields.answer.length : this.fields.answer
        },
        answersCount() {
            return this.poll.pollsAnswers.reduce((acc, item) => acc + item.poll_answer_count, 0)
        },
        customAnswerIsValid() {
            if (Array.isArray(this.fields.answer)) {
                return this.fields.answer.some(item => item === 'custom') ? !!this.fields.customAnswer : true
            } else {
                return this.fields.answer === 'custom' ? !!this.fields.customAnswer : true
            }
        },
        isCheckboxesValid() {
            if (!Array.isArray(this.fields.answer)) {
                return !!this.fields.answer || 'Answer required'
            } else {
                switch (this.poll.require_answer_option) {
                    case AT_LEAST_TYPE: {
                        return this.poll.num_of_answers <= this.fields.answer.length || `At least ${this.poll.num_of_answers} variants required`
                    }
                    case AT_MOST_TYPE: {
                        return this.poll.num_of_answers >= this.fields.answer.length || `At most ${this.poll.num_of_answers} variants required`
                    }
                    case EXACTLY_TYPE: {
                        return this.poll.num_of_answers === this.fields.answer.length || `Exactly ${this.poll.num_of_answers} variants required`
                    }
                    default: {
                        return null
                    }
                }
            }
        }
    },
    watch: {
        'fields.answer': {
            handler(newValue) {
                if (!this.postId) {
                    const answer = {
                        poll_id: this.poll.poll_id,
                        answer: newValue,
                        error: typeof this.isCheckboxesValid === 'string' ? this.isCheckboxesValid : this.customAnswerIsValid ? null : 'Custom answer cannot be empty'
                    }
                    if (newValue === 'custom') {
                        answer.customAnswer = this.fields.customAnswer
                    }
                    this.$emit('answerChange', answer)
                }
            }
        },
        'fields.customAnswer': {
            handler(newValue) {
                if (!this.postId) {
                    this.$emit('answerChange', {
                        poll_id: this.poll.poll_id,
                        answer: 'custom',
                        customAnswer: newValue,
                        error: newValue.length ? null : 'Custom answer cannot be empty'
                    })
                }
            }
        }
    },
    methods: {
        ...mapActions('post', ['votePoll']),

        async submitForm() {
            if (!this.isPollVoting) {
                const payload = {
                    poll_id: this.poll.poll_id
                }
                if (this.fields.answer === 'custom') {
                    payload.custom_answer = this.fields.customAnswer
                } else {
                    payload.poll_answer_id = this.fields.answer
                }
                const isThreads = this.$route.name === 'threads'
                await this.votePoll({poll: payload, postId: this.postId, isThreads})
                if (!isThreads) {
                    this.$emit('pollVoted')
                }
            }
        },
        calcPercentage(answer) {
            const percentage = answer.poll_answer_count / this.answersCount * 100
            if (Number.isInteger(percentage)) {
                return percentage
            } else {
                return percentage.toFixed(1)
            }
        },
        handleSubmit() {
            if (this.isSubmitEnabled) {
                this.isDirty = true
                if (this.customAnswerIsValid && typeof this.isCheckboxesValid !== 'string') {
                    this.validateAndSubmit()
                    if (['threads', 'posts-by-id'].some(item => item === this.$route.name)) {
                        this.sendTrigger({
                            category: 'Content',
                            action: 'click_vote_poll_button_news_feed',
                            module: 'Polls',
                            data: this.poll.poll_id,
                            content_chapter_id: this.post && this.post.chapter_id ? [this.post.chapter_id] : null
                        })
                    }
                }
            }
        }
    },
    mounted() {
        const option = this.poll.require_answer_option
        if (this.poll.num_of_answers === 1 && (option === AT_MOST_TYPE || option === EXACTLY_TYPE)) {
            this.fields.answer = null
        } else {
            this.fields.answer = []
        }
    }
}
</script>

<style lang="scss">
.poll-form {
    padding-top: 10px;
    font-family: Montserrat, sans-serif;

    div {
        width: auto !important;
        max-width: none !important;
    }

    &-event {
        background-color: #F3F3F6;
        border-radius: 10px;
        border: 1px solid #E2E2E2;
        margin-bottom: 12px;
        padding: 16px;
    }

    &__title {
        font-size: 18px;
        font-weight: 700;
        color: var(--accent-color);
        line-height: 21.94px;
        margin-bottom: 10px !important;
    }

    &__answer {
        i {
            font-size: 20px !important;
            color: #E2E2E2 !important;
        }

        label {
            font-size: 14px;
            line-height: 17px;
            color: var(--accent-color) !important;
            word-break: break-word;
        }
    }

    &__custom-answer {
        border-bottom: 1px solid #E2E2E2;

        input {
            padding: 0 !important;
        }

        .v-input__slot::after {
            border-color: var(--accent-color) !important;
        }
    }
    .v-input--is-label-active {
        i {
            color: var(--accent-color) !important;
        }
    }

    &__submit-btn {
        width: 100%;
        height: 50px !important;
        border-radius: 8px;
        font-size: 18px !important;
        line-height: 22px;
        font-weight: 700;
        text-transform: capitalize;
        background-color: var(--button-main-background-color) !important;
        color: var(--button-main-color) !important;

        &-disabled {
            opacity: 0.5;
            cursor: default;
        }

        .v-progress-circular {
            width: 23px !important;
        }
    }

    &__error {
        position: absolute;
        top: -8px;
        left: 50%;
        transform: translateX(-50%);
        font-size: 12px;
        color: red;
        margin: 0;
    }
}

.poll-result {
    padding-top: 10px;
    font-family: Montserrat, sans-serif;

    &-event {
        padding: 16px;
        background-color: #F3F3F6 !important;
        margin-bottom: 10px;
        border-radius: 10px !important;
        border: 1px solid #E2E2E2 !important;

        .v-progress-linear__buffer {
            background-color: #fff !important;
        }
    }

    &__percentage, &__answer {
        display: inline-block;
        font-size: 14px;
        font-weight: 500;
        line-height: 17px;
        color: var(--accent-color);
        min-width: 37px;
    }

    &__percentage {
        white-space: nowrap;
    }

    &__answer {
        word-break: break-word;
    }

    &__answer-scale {
        .v-progress-linear__background {
            background-color: #F3F3F6 !important;
            opacity: 1 !important;
        }
    }
}
</style>